import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Deployment, UsageReport } from "@/types";

// Alle Deployments einer Lizenz (Sub-Resource).
export function useDeployments(licenseId: string | undefined): UseQueryResult<Deployment[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: licenseId
      ? queryKeys.subList("licenses", licenseId, "deployments")
      : ["resources", "licenses", "_invalid", "deployments", "list"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Deployment[]>(`/licenses/${licenseId}/deployments`);
    },
    enabled: !!licenseId && !!session,
  });
}

// Usage-Zeitreihe eines Deployments (neueste zuerst).
export function useDeploymentUsage(
  deploymentId: string | undefined,
  limit = 50,
): UseQueryResult<UsageReport[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: deploymentId
      ? [...queryKeys.detail("deployments", deploymentId), "usage", limit]
      : ["resources", "deployments", "_invalid", "usage"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<UsageReport[]>(`/deployments/${deploymentId}/usage?limit=${limit}`);
    },
    enabled: !!deploymentId && !!session,
  });
}
